/* eslint-disable @atlassian/tangerine/import/no-parent-imports */

import React, { useMemo } from 'react';

import { Inline } from '@atlaskit/primitives/compiled';

import { universalTypeFilterId } from '../../../../common/constants/filters';
import { type ProductKeys } from '../../../../common/constants/product-keys';
import AppFilterTabsInline from '../../../../common/ui/filters/app-filter-tabs-inline';
import { ErrorBoundary } from '../../../../controllers/error-boundary';
import { useSelectFilter } from '../../../../controllers/filters/base-select-filter';
import { useIsUserOAuthed, useOAuthHandlers } from '../../../../controllers/oauth';
import { useBootstrap, useFeatures } from '../../../../controllers/store/bootstrap';
import { useSearchActions, useSelectedProduct } from '../../../../controllers/store/search';

/**
 * Render search page product filters as tabs.
 */
const AppFilterInline = (): React.JSX.Element => {
	const [{ products: availableProducts, nouns: availableNouns, thirdPartyConfigs }] =
		useBootstrap();
	const [selectedProduct] = useSelectedProduct();
	const [universalTypeFilterData] = useSelectFilter({ filterId: universalTypeFilterId });

	const { selectProductFilter, unselectProductFilter, unsafe_setAtlasNouns } = useSearchActions();
	const [features] = useFeatures();

	const appFilterProps = useMemo(
		() => ({
			sideBySide: features?.sideBySide?.enabled,
			availableProducts,
			availableNouns,
			selectedProduct,
			filterData: universalTypeFilterData,
			setAtlasNoun: unsafe_setAtlasNouns,
			onSelectProduct: (newProduct: ProductKeys | undefined) => {
				if (newProduct === selectedProduct) {
					// do nothing when the same product is selected
					return;
				}

				if (!newProduct && selectedProduct) {
					unselectProductFilter();
					return;
				}

				selectProductFilter(newProduct);
			},
			useOAuthHandlers,
			useIsUserOAuthed,
			thirdPartyConfigs,
		}),
		[
			features,
			availableProducts,
			availableNouns,
			selectedProduct,
			universalTypeFilterData,
			unsafe_setAtlasNouns,
			selectProductFilter,
			unselectProductFilter,
			thirdPartyConfigs,
		],
	);

	return (
		<Inline space="space.100" shouldWrap grow="fill">
			<ErrorBoundary section="SearchPageAppFilterInline">
				{features?.sideBySide?.enabled ? <></> : <AppFilterTabsInline {...appFilterProps} />}
			</ErrorBoundary>
		</Inline>
	);
};

export default AppFilterInline;
